import type {
  AcademyWorksheetShareDetailSetting,
  CommonResponseAcademyResponse,
  CommonResponseB2GContractPdfResponse,
  CommonResponseB2GContractResponse,
  GetEncodedNiceInfoData,
  ProcessByTypeData,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  type Academy,
  type AcademyDetail,
  AcademyDetailSchema,
  AcademySchema,
  type AcademyWorksheetShareSetting,
  AcademyWorksheetShareSettingSchema,
  type B2gContract,
  type B2gContractPdf,
  B2gContractPdfSchema,
  B2gContractSchema,
  type NiceEncodeData,
  NiceEncodeDataSchema,
  type NiceIdentity,
  NiceIdentitySchema,
} from './schemas'

// 생성 타입과 실제 와이어가 어긋나는 필드:
//   email·subscribePlan·subscribeItems·academyGroupType·signUpFrom·academyAdditionalData.organizationName
//     — 생성 타입은 optional(키 생략)로 그리지만 서버는 키를 빼지 않고 null 을 싣는다
// 2026-09-30 백엔드 develop(d90e66b2c) academy/controller/response/AcademyResponse.kt·AcademyGroupType.kt 선언과,
// 팀이 녹음한 dev 응답 e2e 목(apps/mathflat-*/__tests__/e2e/_mocks/data/auth/academies.json, academyGroupType·
// signUpFrom·organizationName 이 null)으로 확인. 스키마가 실제 와이어를 검증하므로 입력을 이 한 자리에서 unknown 으로 넘긴다.
const asWire = (raw: CommonResponseAcademyResponse['data']): unknown => raw

export const adaptAcademy = (raw: CommonResponseAcademyResponse['data']): Academy =>
  parseApiResponse(AcademySchema, asWire(raw), 'Academy')

// 상세는 위 필드에 더해 주소·각종 기간·판매자 코드·구독 인원 제한·추가 요금 기준과 부가 정보의 인증·조직 필드를
// 생성 타입이 optional 로 그리지만, 서버는 키를 빼지 않고 null 을 싣는다(같은 근거).
export const adaptAcademyDetail = (raw: CommonResponseAcademyResponse['data']): AcademyDetail =>
  parseApiResponse(AcademyDetailSchema, asWire(raw), 'Academy')

// B2G 학원이 아니거나 계약이 없으면 서버가 data 를 null 로 낸다. 생성 타입은 null 을 그리지 않고, pdfUrl 도
// optional 로 그리지만 키를 빼지 않고 null 을 싣는다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의
// academy/service/B2gContractService.kt(getB2gContract)·controller/response/B2gContractResponse.kt 로 확인.
const asWireB2gContract = (raw: CommonResponseB2GContractResponse['data']): unknown => raw

export const adaptB2gContract = (
  raw: CommonResponseB2GContractResponse['data'],
): B2gContract | null =>
  parseApiResponse(B2gContractSchema.nullable(), asWireB2gContract(raw), 'Academy')

export const adaptB2gContractPdf = (
  raw: CommonResponseB2GContractPdfResponse['data'],
): B2gContractPdf => parseApiResponse(B2gContractPdfSchema, raw, 'Academy')

export const adaptAcademyWorksheetShareSetting = (
  raw: AcademyWorksheetShareDetailSetting,
): AcademyWorksheetShareSetting =>
  parseApiResponse(AcademyWorksheetShareSettingSchema, raw, 'Academy')

// 서버 컨트롤러가 인증 목적마다 다른 값을 내려 `Any?` 로 선언해 생성 타입이 data 를 object 로만 그린다. 정산 정보 등록
// 목적이면 NiceDecodedResponse 가 실린다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의 academy/controller/AcademyController.kt
// (processByType)·service/NiceService.kt 로 확인.
const asWireNiceIdentity = (raw: ProcessByTypeData['data']): unknown => raw

export const adaptNiceIdentity = (raw: ProcessByTypeData['data']): NiceIdentity =>
  parseApiResponse(NiceIdentitySchema, asWireNiceIdentity(raw), 'Academy')

export const adaptNiceEncodeData = (raw: GetEncodedNiceInfoData['data']): NiceEncodeData =>
  parseApiResponse(NiceEncodeDataSchema, raw, 'Academy')
